import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'
import { useState } from 'react'

import cameraIcon from '@assets/images/camera-icon.svg'
import penIcon from '@assets/images/pen-icon.svg'
import trashIcon from '@assets/images/trash-icon.svg'
import uploadIcon from '@assets/images/upload-icon.svg'

import { mapFileToLocalFile } from '@utils/mappers'
import { useCameraPermissions, usePlatformDetection } from '@utils/hooks'

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

import { LocalFile, RemoteFile } from '@types'

import {
  ButtonFileRow,
  CameraImg,
  Container,
  DropAreaContainer,
  DropAreaInput,
  FileGridItem,
  FileName,
  FileOverlay,
  FilePreview,
  FilesGridContainer,
  HeaderContainer,
  HeaderLabel,
  HintLabel,
  ImgFileRow,
  UploadButtonContainer,
  UploadButtonInput,
  UploadButtonLabel,
  UploadImg,
} from './UploadPicture.styled'

const AUTHORIZED_FILE_EXTENSIONS = 'image/jpeg, image/png, image/jpg'

export interface Props {
  label: string
  files: (LocalFile | RemoteFile)[]
  setFiles: (files: (LocalFile | RemoteFile)[]) => void
  multiple?: boolean
  disabled?: boolean
  required?: boolean
}

export const UploadPicture = (props: Props): JSX.Element => {
  const { label, files, setFiles, required, multiple, disabled } = props
  const [isCapturing, setIsCapturing] = useState(false)

  const { t } = useTranslation()

  const { isAndroid, isNative } = usePlatformDetection()
  const { permissionState, requestPermissions, checkPermissions, resetError } = useCameraPermissions()

  const filteredFiles = files.filter((file) =>
    Object.hasOwn(file, 'deleted') && (file as RemoteFile).deleted == true ? false : true,
  )

  const takePhoto = async () => {
    try {
      setIsCapturing(true)
      resetError()

      let hasPermission = await checkPermissions()

      if (!hasPermission) {
        hasPermission = await requestPermissions()
      }

      if (!hasPermission) return

      const image = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
        promptLabelHeader: t('components_UploadPicture_camera_prompt_label_header'),
        promptLabelPhoto: t('components_UploadPicture_camera_prompt_label_photo'),
        promptLabelPicture: t('components_UploadPicture_camera_prompt_label_picture'),
        presentationStyle: 'popover',
        promptLabelCancel: t('components_UploadPicture_camera_prompt_label_cancel'),
        webUseInput: true,
      })

      if (image.webPath) {
        const response = await fetch(image.webPath)
        const blob = await response.blob()
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' })

        const localFile = mapFileToLocalFile(file) as LocalFile
        setFiles([...files, localFile])
      }
    } catch (error) {
      console.error('Error while taking photo:', error)
    } finally {
      setIsCapturing(false)
    }
  }

  const handleInputInteraction = async (
    e: React.MouseEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (isNative && isAndroid) {
      if (e.type === 'click') {
        e.preventDefault()
        await takePhoto()
      }
    } else {
      if (e.type === 'change') {
        const changeEvent = e as React.ChangeEvent<HTMLInputElement>
        const _files = [...(changeEvent.target.files || [])].map((_file) => mapFileToLocalFile(_file)) as LocalFile[]
        setFiles([...files, ..._files])
      }
    }
  }

  const isButtonDisabled = disabled || isCapturing || permissionState.isLoading

  return (
    <Container>
      <HeaderContainer>
        <HeaderLabel>{`${label}${
          filteredFiles.length > 0 && multiple ? ` (${filteredFiles.length})` : ''
        }`}</HeaderLabel>
        {!disabled && !(!multiple && filteredFiles.length > 0) && (
          <UploadButtonContainer>
            <UploadButtonInput
              key={v4()}
              name="picture-uploader"
              required={filteredFiles.length > 0 ? false : required}
              multiple={multiple}
              disabled={isButtonDisabled}
              type="file"
              accept={AUTHORIZED_FILE_EXTENSIONS}
              onClick={handleInputInteraction}
              onChange={handleInputInteraction}
            />

            <UploadButtonLabel disabled={isButtonDisabled}>
              <UploadImg src={uploadIcon} />
              {t('components_UploadPicture_label_upload')}
            </UploadButtonLabel>
          </UploadButtonContainer>
        )}
      </HeaderContainer>

      {permissionState.error && (
        <div style={{ color: 'red', fontSize: '14px', marginBottom: '8px' }}>{permissionState.error}</div>
      )}

      {filteredFiles.length > 0 ? (
        <FilesGridContainer disabled={disabled}>
          {filteredFiles.map((file) => (
            <FileGridItem key={file.id} disabled={disabled}>
              <FilePreview
                src={file.url}
                crossOrigin={file?.url?.includes('s3') ? undefined : 'anonymous'}
                loading="lazy"
                disabled={disabled}
                onClick={() => {
                  window.open(file.url, '_blank')
                }}
              />

              <FileName
                onClick={() => {
                  window.open(file.url, '_blank')
                }}
              >
                {file.name}
              </FileName>

              {!disabled && (
                <FileOverlay disabled={disabled}>
                  <ButtonFileRow>
                    <UploadButtonInput
                      type="file"
                      accept={AUTHORIZED_FILE_EXTENSIONS}
                      onChange={(e) => {
                        const newFile = e.target.files?.[0]
                        if (newFile) {
                          const mappedFiles = files.map((_file) =>
                            file.id === _file.id ? mapFileToLocalFile(newFile) : _file,
                          )

                          if (Object.hasOwn(file, 'deleted')) {
                            setFiles([...mappedFiles, { ...file, deleted: true } as RemoteFile])
                          } else {
                            setFiles([...mappedFiles])
                          }
                        }
                      }}
                    />
                    <ImgFileRow src={penIcon} />
                  </ButtonFileRow>
                  <ButtonFileRow
                    onClick={() => {
                      if (Object.hasOwn(file, 'deleted')) {
                        const mappedFiles = files.map((_file) =>
                          file.id === _file.id ? ({ ...file, deleted: true } as RemoteFile) : _file,
                        )
                        setFiles(mappedFiles)
                      } else {
                        const mappedFiles = files.filter((_file) => file.id !== _file.id)
                        setFiles(mappedFiles)
                      }
                    }}
                  >
                    <ImgFileRow src={trashIcon} />
                  </ButtonFileRow>
                </FileOverlay>
              )}
            </FileGridItem>
          ))}
        </FilesGridContainer>
      ) : (
        <DropAreaContainer
          disabled={disabled}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()

            const droppedFiles = [...(e.dataTransfer.files || [])]
            const filesToAdd: File[] = []

            droppedFiles.forEach((file) => {
              if (AUTHORIZED_FILE_EXTENSIONS.includes(file.type.toLowerCase())) {
                filesToAdd.push(file)

                if (!multiple) {
                  return
                }
              }
            })

            const _files = [...(filesToAdd || [])].map((_file) => mapFileToLocalFile(_file)) as LocalFile[]
            setFiles(_files)
          }}
        >
          <DropAreaInput
            disabled={isButtonDisabled}
            type="file"
            accept={AUTHORIZED_FILE_EXTENSIONS}
            onClick={handleInputInteraction}
            onChange={handleInputInteraction}
          />
          <CameraImg src={cameraIcon} />
        </DropAreaContainer>
      )}

      {!disabled && <HintLabel>{t('components_UploadPicture_label_hint')}</HintLabel>}
    </Container>
  )
}
